import React from 'react';
import { Dialog, DialogHeader, DialogTitle, DialogContent } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { AlertTriangle, Lightbulb, ArrowLeft } from 'lucide-react';
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from '../../../../../lib/utils';

export interface ValidationError {
  parameter: string;
  minValue: number | string;
  maxValue: number | string;
  unit?: string;
  passGroup?: string;
  type?: 'min_max_mismatch' | 'negative_value' | 'zero_values' | 'out_of_range' | 'no_parameters_selected';
  jobNumber?: string;
}

interface ValidationErrorModalProps {
  isOpen: boolean;
  onClose: () => void;
  errors: ValidationError[];
  jobNumber: string;
  calculationLevel: string;
  onErrorsResolved?: (resolvedErrors: ValidationError[]) => void;
}

export const ValidationErrorModal: React.FC<ValidationErrorModalProps> = ({
  isOpen,
  onClose,
  errors,
  jobNumber,
  calculationLevel,
  onErrorsResolved
}) => {

  // Add custom scrollbar styles
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .validation-error-list::-webkit-scrollbar {
        width: 12px;
      }
      .validation-error-list::-webkit-scrollbar-track {
        background: #f3f4f6;
        border-radius: 6px;
        margin: 4px;
      }
      .validation-error-list::-webkit-scrollbar-thumb {
        background: #9ca3af;
        border-radius: 6px;
        border: 2px solid #f3f4f6;
      }
      .validation-error-list::-webkit-scrollbar-thumb:hover {
        background: #6b7280;
      }
      .validation-error-list::-webkit-scrollbar-corner {
        background: #f3f4f6;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  if (errors.length === 0) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-50 grid w-full max-w-[100vw] h-screen max-h-screen translate-x-[-50%] translate-y-[-50%] gap-0 border bg-background p-0 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
          )}
        >
          <div className="flex flex-col h-full">
            <DialogHeader className="px-6 py-4 border-b flex flex-row items-center justify-between bg-white">
              <div className="flex items-center gap-4">
                <Button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  variant="outline"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-500" />
                </Button>
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-8 h-8 bg-red-100 rounded-full">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <DialogTitle className="text-lg font-bold text-gray-900">
                      Validation Errors
                    </DialogTitle>
                  </div>
                </div>
              </div>
            </DialogHeader>

            <DialogContent className="flex-1 overflow-y-auto p-4">
              <div className="space-y-3">
                {/* Summary */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <div className="text-sm text-gray-600">
                    Job Number: {jobNumber} | Level: {calculationLevel}
                  </div>
                </div>

                {/* Error Summary */}
                {errors.some(error => error.type === 'no_parameters_selected') && (
                  <div className="bg-red-50 border rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="h-4 w-4 text-red-600" />
                      <div className="text-sm text-red-800">
                        <span className="font-medium">Parameter Selection Required:</span> The following jobs need at least one parameter selected:
                      </div>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {errors
                        .filter(error => error.type === 'no_parameters_selected')
                        .map((error, index) => (
                          <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-readable-red bg-[rgba(196,0,31,0.06)]">
                            {error.jobNumber}
                          </span>
                        ))}
                    </div>
                  </div>
                )}

                {/* Error List */}
                <div className="relative">
                  <div 
                    className="validation-error-list space-y-3 max-h-96 overflow-y-auto border border-gray-200 rounded-lg p-4 bg-gray-50 shadow-inner"
                  >

                    {errors.map((error, index) => (
                      <div key={index} className="border rounded-lg p-4 bg-red-50 hover:bg-red-100 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="font-medium text-gray-900 mb-2 text-sm">
                              {error.parameter}
                              {error.passGroup && (
                                <span className="text-xs text-gray-600 ml-2">({error.passGroup})</span>
                              )}
                              {error.jobNumber && (
                                <span className="text-xs text-red-600 ml-2">[Job: {error.jobNumber}]</span>
                              )}
                            </div>
                            {error.type === 'negative_value' ? (
                              <div className="text-sm">
                                <span className="text-red-600 font-semibold">
                                  Value: {error.minValue}{error.unit ? ` ${error.unit}` : ''}
                                </span>
                              </div>
                            ) : error.type === 'out_of_range' ? (
                              <div className="text-sm">
                                <span className="text-red-600 font-semibold">
                                  Value: {error.minValue}{error.unit ? ` ${error.unit}` : ''}
                                </span>
                              </div>
                            ) : error.type === 'no_parameters_selected' ? (
                              <div className="text-sm">
                                <span className="text-red-600 font-semibold">
                                  No parameters selected
                                </span>
                              </div>
                            ) : error.type === 'zero_values' ? (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-red-600 font-semibold">
                                  Min: {error.minValue}{error.unit ? ` ${error.unit}` : ''}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span className="text-red-600 font-semibold">
                                  Max: {error.maxValue}{error.unit ? ` ${error.unit}` : ''}
                                </span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-sm">
                                <span className="text-red-600 font-semibold">
                                  Min: {error.minValue}{error.unit ? ` ${error.unit}` : ''}
                                </span>
                                <span className="text-gray-400">→</span>
                                <span className="text-red-600 font-semibold">
                                  Max: {error.maxValue}{error.unit ? ` ${error.unit}` : ''}
                                </span>
                              </div>
                            )}
                            <div className="text-xs text-red-600 mt-2">
                              {error.type === 'negative_value' 
                                ? 'Value cannot be negative' 
                                : error.type === 'out_of_range'
                                ? 'Value is out of allowed range'
                                : error.type === 'no_parameters_selected'
                                ? error.jobNumber 
                                  ? `At least one parameter must be selected for Job ${error.jobNumber}`
                                  : 'At least one parameter must be selected'
                                : error.type === 'zero_values'
                                ? 'Both Min and Max values cannot be zero'
                                : 'Min value should be less than Max value'
                              }
                            </div>
                          </div>
                          <div className="ml-4 flex items-center gap-2">
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium text-readable-red bg-[rgba(196,0,31,0.06)]">
                              Root
                            </span>
                            {onErrorsResolved && (
                              <Button
                                onClick={() => onErrorsResolved([error])}
                                size="sm"
                                variant="outline"
                                className="text-xs h-7 px-2"
                              >
                                Resolve
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}

                  </div>
                  

                </div>

                {/* Tip */}
                <div className="bg-green-50 border rounded-lg p-3">
                  <div className="flex items-start gap-2">
                    <Lightbulb className="h-4 w-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <div className="text-xs text-green-800">
                      <span className="font-medium">Tip:</span> Please correct the Min/Max values to ensure Min is less than Max.
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {onErrorsResolved && (
                  <div className="flex justify-end gap-3">
                    <Button
                      onClick={() => onErrorsResolved(errors)}
                      variant="default"
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Resolve All Errors
                    </Button>
                  </div>
                )}
              </div>
            </DialogContent>


          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </Dialog>
  );
};
